import {
  AloftPrice,
  PairedCostRouteItem,
  RouteItem,
  schAloftPrice,
} from '@aloft/toolkit-shared';

import { fieldDisplayNames, VALIDATION_STEP_LABELS } from '../../../../hooks/validation/consts.ts';
import { schAppraisifyValidation } from '../../../../hooks/validation/schemas.ts';
import { selectPropertyDataEntities } from '../../../../redux/features/selectors/propertyDataSelectors/propertyDataSelectors.ts';
import { selectImprovementsEntities } from '../../../../redux/features/selectors/propertyDataSelectors/improvementsSelectors.ts';
import { selectReconciliation } from '../../../../redux/features/selectors/selectReconciliation.ts';
import { selectAllCompTypesReconciliationData } from '../../../../redux/features/selectors/selectReconciliationData.ts';
import { OrderState, SubjectPropertyDataState } from '../../../../redux/types.ts';
import { toOrderDetailsTargetCellKey } from '../../../orderDetails/orderDetailsFieldFocus.ts';
import { toRequiredFieldTargetCellKey } from '../../requiredFieldFocus.ts';

const ORDER_DETAILS_FIELD_ORDER = [
  'zoning',
  'zoningCompliance',
  'parcelNumber',
  'legalDescription',
  'locationType',
  'hoa',
  'effectiveDate',
  'intendedUsers',
  'intendedUse',
  'totalRenovationBudget',
  'propertyInterest',
  'propertyInterestOther',
] as const;

const COMMENTARY_FIELD_ORDER = [
  'estimatedDemand',
  'estimatedExposureTime',
  'marketConditionCommentary',
  'priorSalesCommentary',
] as const;

const RECONCILIATION_FIELD_ORDER = [
  'totalCostToCure',
  'totalCostToCureCommentary',
  'opinionOfMarketValue',
  'aivDiscrepancyCommentary',
  'opinionOfAfterRepairValue',
  'arvDiscrepancyCommentary',
  'totalRenovationBudget',
  'feasibilityCommentary',
  'revisionCommentary',
  'alternativeComparablesCommentary',
  'reconciliationCommentary',
  'aivReconciliationCommentary',
  'arvReconciliationCommentary',
  'valueTypes',
  'highestAndBestUseCommentary',
] as const;

export type IncompleteRequiredFieldPage =
  | 'details'
  | 'cost-approach'
  | 'appraisify-commentary'
  | 'reconciliation';

const PAGE_ROUTE_ORDER: IncompleteRequiredFieldPage[] = [
  'details',
  'cost-approach',
  'appraisify-commentary',
  'reconciliation',
];

export type IncompleteRequiredFieldIssue = {
  fieldKey: string;
  displayName: string;
  page: IncompleteRequiredFieldPage;
  targetCellKey?: string;
  propertyID?: string;
};

export type IncompleteRequiredFieldsInput = {
  order: OrderState;
  propertyDataEntities: ReturnType<typeof selectPropertyDataEntities>;
  compSequence: string[];
  improvements?: ReturnType<typeof selectImprovementsEntities>;
  includeCostApproach?: boolean;
  opinionOfSiteValue?: unknown;
  marketConditionsV2?: {
    estimatedDemandSupply?: string;
    estimatedExposureTime?: string;
    commentary?: string;
  };
  priorSales?: { commentary?: string };
  reconciliationCommentary?: {
    totalCostToCureCommentary?: string;
    reconciliationCommentary?: string;
    aivReconciliationCommentary?: string;
    arvReconciliationCommentary?: string;
    highestAndBestUseCommentary?: string;
    revisionCommentary?: string;
    alternativeComparablesCommentary?: string[];
    feasibilityCommentary?: string;
    aivDiscrepancyCommentary?: string;
    arvDiscrepancyCommentary?: string;
  };
  reconciliation: ReturnType<typeof selectReconciliation>;
  hasAivDiscrepancy: boolean;
  hasArvDiscrepancy: boolean;
  isMockOrder: boolean;
  reconciliationData?: ReturnType<typeof selectAllCompTypesReconciliationData>;
};

const PAGE_TITLES: Record<IncompleteRequiredFieldPage, string> = {
  details: VALIDATION_STEP_LABELS.OrderDetails,
  'cost-approach': VALIDATION_STEP_LABELS.Analysis,
  'appraisify-commentary': VALIDATION_STEP_LABELS.Commentary,
  reconciliation: VALIDATION_STEP_LABELS.Reconciliation,
};

export const getIncompleteRequiredFieldsMessage = (page: IncompleteRequiredFieldPage) =>
  `Please complete all required fields on the ${PAGE_TITLES[page]} page before proceeding to the next step.`;

const formatFieldKeyForDisplay = (key: string) =>
  key
    .replace(/_/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (c) => c.toUpperCase());

const getDisplayName = (fieldKey: string) =>
  fieldDisplayNames[fieldKey] ?? formatFieldKeyForDisplay(fieldKey);

const buildIssue = (
  fieldKey: string,
  page: IncompleteRequiredFieldPage,
  options?: { propertyID?: string },
): IncompleteRequiredFieldIssue => {
  let targetCellKey: string | undefined;
  if (page === 'details') {
    targetCellKey = toOrderDetailsTargetCellKey(fieldKey);
  } else if (page === 'appraisify-commentary' || page === 'reconciliation') {
    targetCellKey = toRequiredFieldTargetCellKey(page, fieldKey);
  }

  return {
    fieldKey,
    displayName: getDisplayName(fieldKey),
    page,
    targetCellKey,
    propertyID: options?.propertyID,
  };
};

const sortIssues = (issues: IncompleteRequiredFieldIssue[]) => {
  const pageOrder = new Map<string, number>(PAGE_ROUTE_ORDER.map((page, index) => [page, index]));
  const orderDetailsOrder = new Map<string, number>(
    ORDER_DETAILS_FIELD_ORDER.map((field, index) => [field, index]),
  );
  const commentaryOrder = new Map<string, number>(
    COMMENTARY_FIELD_ORDER.map((field, index) => [field, index]),
  );
  const reconciliationOrder = new Map<string, number>(
    RECONCILIATION_FIELD_ORDER.map((field, index) => [field, index]),
  );

  const fieldOrderForPage = (page: IncompleteRequiredFieldPage) => {
    switch (page) {
      case 'details':
        return orderDetailsOrder;
      case 'appraisify-commentary':
        return commentaryOrder;
      case 'reconciliation':
        return reconciliationOrder;
      default:
        return new Map<string, number>();
    }
  };

  return [...issues].sort((a, b) => {
    const pageCompare =
      (pageOrder.get(a.page) ?? Number.MAX_SAFE_INTEGER) -
      (pageOrder.get(b.page) ?? Number.MAX_SAFE_INTEGER);
    if (pageCompare !== 0) {
      return pageCompare;
    }
    const order = fieldOrderForPage(a.page);
    return (
      (order.get(a.fieldKey) ?? Number.MAX_SAFE_INTEGER) -
      (order.get(b.fieldKey) ?? Number.MAX_SAFE_INTEGER)
    );
  });
};

type ReconciliationFilterContext = {
  order: IncompleteRequiredFieldsInput['order'];
  includeTotalCostToCure: boolean;
  includeTotalRenovationBudget: boolean;
  includeOpinionOfAfterRepairValue: boolean;
  hasAivDiscrepancy: boolean;
  hasArvDiscrepancy: boolean;
  isMockOrder: boolean;
  shouldShowFeasibilityCommentary: boolean;
  shouldShowRevisionTab: boolean;
  opinionOfMarketValue: unknown;
  opinionOfAfterRepairValue: unknown;
  totalRenovationBudget: unknown;
};

const getReconciliationFieldKey = (path: (string | number)[]) => {
  const field = path[0];
  if (typeof field !== 'string') {
    return undefined;
  }
  if (field === 'alternativeComparablesCommentary') {
    return field;
  }
  return field;
};

const shouldSkipReconciliationIssue = (
  path: (string | number)[],
  context: ReconciliationFilterContext,
) => {
  const field = path[path.length - 1];
  const fieldKey = getReconciliationFieldKey(path);
  if (!fieldKey) {
    return true;
  }

  if (fieldKey === 'reconciliationData') {
    return true;
  }

  if (
    !context.order.hasArvOption &&
    (fieldKey === 'opinionOfAfterRepairValue' ||
      fieldKey === 'totalCostToCure' ||
      fieldKey === 'totalRenovationBudget')
  ) {
    return true;
  }

  if (field === 'revisionCommentary' && !context.shouldShowRevisionTab) {
    return true;
  }

  if (
    fieldKey === 'alternativeComparablesCommentary' &&
    (!context.shouldShowRevisionTab || !context.order.revisionData?.comparables)
  ) {
    return true;
  }

  if (
    !context.includeTotalCostToCure &&
    (fieldKey === 'totalCostToCure' || fieldKey === 'totalCostToCureCommentary')
  ) {
    return true;
  }

  if (fieldKey === 'feasibilityCommentary' && !context.shouldShowFeasibilityCommentary) {
    return true;
  }

  if (fieldKey === 'aivDiscrepancyCommentary' && !context.hasAivDiscrepancy) {
    return true;
  }

  if (fieldKey === 'arvDiscrepancyCommentary' && !context.hasArvDiscrepancy) {
    return true;
  }

  if (
    fieldKey === 'totalRenovationBudget' &&
    (context.includeTotalRenovationBudget || !context.order.hasArvOption)
  ) {
    return true;
  }

  if (
    fieldKey === 'opinionOfAfterRepairValue' &&
    !context.includeOpinionOfAfterRepairValue
  ) {
    return true;
  }

  if (fieldKey === 'aivReconciliationCommentary' && !context.order.hasArvOption) {
    return true;
  }

  if (fieldKey === 'arvReconciliationCommentary' && !context.order.hasArvOption) {
    return true;
  }

  if (fieldKey === 'reconciliationCommentary' && context.order.hasArvOption) {
    return true;
  }

  if (
    fieldKey === 'feasibilityCommentary' &&
    context.opinionOfAfterRepairValue &&
    context.opinionOfMarketValue
  ) {
    const renovationBudget = context.totalRenovationBudget || AloftPrice.zero;
    const roi = AloftPrice.schema
      .parse(context.opinionOfAfterRepairValue)
      .sub(AloftPrice.schema.parse(context.opinionOfMarketValue))
      .sub(AloftPrice.schema.parse(renovationBudget));
    if (roi.toDollarLike() > 0) {
      return true;
    }
  }

  return false;
};

const getOrderDetailsIssues = (
  input: IncompleteRequiredFieldsInput,
): IncompleteRequiredFieldIssue[] => {
  const subjectEntity = input.propertyDataEntities.subject;
  if (!subjectEntity || subjectEntity.id !== 'subject') {
    return [];
  }

  const identification = (subjectEntity as SubjectPropertyDataState).subjectData.editableData
    .propertyIdentification;

  const result = schAppraisifyValidation.shape.orderDetails.safeParse({
    zoning: identification?.zoning ?? '',
    zoningCompliance: identification?.zoningCompliance ?? '',
    parcelNumber: identification?.parcelNumber ?? '',
    newConstruction: identification?.newConstruction ?? false,
    legalDescription: identification?.legalDescription ?? '',
    hoa: identification?.hoa ?? '',
    effectiveDate: input.order.effectiveDate ?? '',
    intendedUsers: input.order.intendedUsers ?? '',
    intendedUse: input.order.intendedUse ?? '',
    locationType: identification?.locationType ?? '',
    propertyInterest: identification?.propertyInterest ?? '',
    propertyInterestOther: identification?.propertyInterestOther ?? '',
  });

  if (result.success) {
    return [];
  }

  return result.error.issues.flatMap((issue) => {
    const fieldKey = issue.path[0];
    if (typeof fieldKey !== 'string') {
      return [];
    }
    return [buildIssue(fieldKey, 'details')];
  });
};

const getCostApproachIssues = (
  input: IncompleteRequiredFieldsInput,
): IncompleteRequiredFieldIssue[] => {
  if (!input.includeCostApproach) {
    return [];
  }

  const issues: IncompleteRequiredFieldIssue[] = [];
  const { improvements, compSequence, opinionOfSiteValue } = input;

  const subjectQuality = improvements?.subject?.craftsmanQuality;
  const missingCompId = compSequence.find((id) => !improvements?.[id]?.craftsmanQuality);

  if (!subjectQuality || missingCompId) {
    issues.push(
      buildIssue('costQuality', 'cost-approach', {
        propertyID: !subjectQuality ? 'subject' : missingCompId,
      }),
    );
  }

  const analysisResult = schAppraisifyValidation.shape.analysis.safeParse({
    comps: compSequence,
    opinionOfSiteValue: opinionOfSiteValue ?? '',
  });

  if (!analysisResult.success) {
    const hasOpinionOfSiteValueIssue = analysisResult.error.issues.some(
      (issue) => issue.path[issue.path.length - 1] === 'opinionOfSiteValue',
    );
    if (hasOpinionOfSiteValueIssue) {
      issues.push(buildIssue('opinionOfSiteValue', 'cost-approach'));
    }
  }

  return issues;
};

const hasRenovationBudget = (totalRenovationBudget: unknown) =>
  schAloftPrice.safeParse(totalRenovationBudget).success;

const getRenovationBudgetIssues = (
  input: IncompleteRequiredFieldsInput,
): IncompleteRequiredFieldIssue[] => {
  if (input.isMockOrder) {
    return [];
  }

  const { order, reconciliation } = input;
  const { totalRenovationBudget, includeTotalRenovationBudget } = reconciliation;

  if (hasRenovationBudget(totalRenovationBudget)) {
    return [];
  }

  if (includeTotalRenovationBudget) {
    return [buildIssue('totalRenovationBudget', 'details')];
  }

  if (order.hasArvOption) {
    return [buildIssue('totalRenovationBudget', 'reconciliation')];
  }

  return [];
};

export const getIncompleteRequiredFieldPageForRoute = (
  route: RouteItem,
): IncompleteRequiredFieldPage | undefined => {
  if (route === 'details') {
    return 'details';
  }
  if (route === 'cost-approach' || route === 'craftsman-cost-approach') {
    return 'cost-approach';
  }
  if (route === 'appraisify-commentary' || route === 'commentary') {
    return 'appraisify-commentary';
  }
  if (route === 'reconciliation') {
    return 'reconciliation';
  }
  return undefined;
};

export const getIncompleteRequiredFieldIssues = (
  input: IncompleteRequiredFieldsInput,
): IncompleteRequiredFieldIssue[] => {
  const {
    order,
    marketConditionsV2,
    priorSales,
    reconciliationCommentary,
    reconciliation,
    hasAivDiscrepancy,
    hasArvDiscrepancy,
    isMockOrder,
  } = input;

  const emptyReconciliationData = { general: {}, aiv: {}, arv: {} };

  const {
    totalCostToCure,
    opinionOfMarketValue,
    opinionOfAfterRepairValue,
    totalRenovationBudget,
    includeTotalCostToCure,
    includeTotalRenovationBudget,
    includeOpinionOfAfterRepairValue,
    valueTypes,
  } = reconciliation;

  const isArvOrder = order.hasArvOption;
  const estimatedROI =
    isArvOrder && opinionOfAfterRepairValue && opinionOfMarketValue && totalRenovationBudget
      ? schAloftPrice
          .parse(opinionOfAfterRepairValue)
          .sub(schAloftPrice.parse(opinionOfMarketValue))
          .sub(schAloftPrice.parse(totalRenovationBudget))
      : null;

  const shouldShowFeasibilityCommentary =
    isArvOrder && estimatedROI !== null && estimatedROI.toDollarLike() <= 0;
  const shouldShowRevisionTab = order.isRevision || !!order.revisionData?.revisionType;

  const orderDetailsIssues = getOrderDetailsIssues(input);
  const costApproachIssues = getCostApproachIssues(input);
  const renovationBudgetIssues = getRenovationBudgetIssues(input);

  const commentaryResult = schAppraisifyValidation.shape.commentary.safeParse({
    estimatedDemand: marketConditionsV2?.estimatedDemandSupply ?? '',
    estimatedExposureTime: marketConditionsV2?.estimatedExposureTime ?? '',
    marketConditionCommentary: marketConditionsV2?.commentary ?? '',
    priorSalesCommentary: priorSales?.commentary ?? '',
  });

  const commentaryIssues: IncompleteRequiredFieldIssue[] = commentaryResult.success
    ? []
    : commentaryResult.error.issues.flatMap((issue) => {
        const fieldKey = issue.path[0];
        if (typeof fieldKey !== 'string') {
          return [];
        }
        return [buildIssue(fieldKey, 'appraisify-commentary')];
      });

  const reconciliationFilterContext: ReconciliationFilterContext = {
    order,
    includeTotalCostToCure: includeTotalCostToCure ?? false,
    includeTotalRenovationBudget: includeTotalRenovationBudget ?? false,
    includeOpinionOfAfterRepairValue: includeOpinionOfAfterRepairValue ?? false,
    hasAivDiscrepancy,
    hasArvDiscrepancy,
    isMockOrder,
    shouldShowFeasibilityCommentary,
    shouldShowRevisionTab,
    opinionOfMarketValue,
    opinionOfAfterRepairValue,
    totalRenovationBudget,
  };

  const reconciliationResult = schAppraisifyValidation.shape.reconciliation.safeParse({
    totalCostToCure,
    totalCostToCureCommentary: reconciliationCommentary?.totalCostToCureCommentary ?? '',
    opinionOfMarketValue,
    opinionOfAfterRepairValue,
    totalRenovationBudget,
    includeTotalRenovationBudget: includeTotalRenovationBudget ?? false,
    includeOpinionOfAfterRepairValue: includeOpinionOfAfterRepairValue ?? false,
    includeTotalCostToCure: includeTotalCostToCure ?? false,
    hasArvOption: order.hasArvOption,
    reconciliationCommentary: reconciliationCommentary?.reconciliationCommentary ?? '',
    aivReconciliationCommentary:
      reconciliationCommentary?.aivReconciliationCommentary ??
      reconciliationCommentary?.reconciliationCommentary ??
      '',
    arvReconciliationCommentary: reconciliationCommentary?.arvReconciliationCommentary ?? '',
    highestAndBestUseCommentary: reconciliationCommentary?.highestAndBestUseCommentary ?? '',
    revisionCommentary: reconciliationCommentary?.revisionCommentary ?? '',
    alternativeComparablesCommentary: reconciliationCommentary?.alternativeComparablesCommentary ?? [],
    feasibilityCommentary: reconciliationCommentary?.feasibilityCommentary ?? '',
    aivDiscrepancyCommentary: reconciliationCommentary?.aivDiscrepancyCommentary ?? '',
    arvDiscrepancyCommentary: reconciliationCommentary?.arvDiscrepancyCommentary ?? '',
    hasAivDiscrepancy,
    hasArvDiscrepancy,
    isMockOrder,
    valueTypes: valueTypes ?? [],
    reconciliationData: input.reconciliationData ?? emptyReconciliationData,
  });

  const reconciliationIssues: IncompleteRequiredFieldIssue[] =
    isMockOrder || reconciliationResult.success
      ? []
      : reconciliationResult.error.issues.flatMap((issue) => {
          if (shouldSkipReconciliationIssue(issue.path, reconciliationFilterContext)) {
            return [];
          }
          const fieldKey = getReconciliationFieldKey(issue.path);
          if (!fieldKey) {
            return [];
          }
          return [buildIssue(fieldKey, 'reconciliation')];
        });

  const uniqueIssues = new Map<string, IncompleteRequiredFieldIssue>();
  [
    ...orderDetailsIssues,
    ...costApproachIssues,
    ...renovationBudgetIssues,
    ...commentaryIssues,
    ...reconciliationIssues,
  ].forEach((issue) => {
    uniqueIssues.set(`${issue.page}:${issue.fieldKey}`, issue);
  });

  return sortIssues([...uniqueIssues.values()]);
};

export const getFirstIncompleteRequiredFieldPage = (
  issues: IncompleteRequiredFieldIssue[],
): IncompleteRequiredFieldPage | undefined =>
  PAGE_ROUTE_ORDER.find((page) => issues.some((issue) => issue.page === page));

export const incompleteRequiredFieldsAppliesToRoute = (
  issues: IncompleteRequiredFieldIssue[],
  route: RouteItem,
): boolean => {
  const firstPage = getFirstIncompleteRequiredFieldPage(issues);
  const page = getIncompleteRequiredFieldPageForRoute(route);
  if (!firstPage || !page) {
    return false;
  }
  return page === firstPage;
};

export const getIncompleteRequiredFieldPages = (
  issues: IncompleteRequiredFieldIssue[],
): PairedCostRouteItem[] => {
  const firstPage = getFirstIncompleteRequiredFieldPage(issues);
  return firstPage ? [firstPage] : [];
};
